/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action, computed } from '@ember/object';
import { task } from 'ember-concurrency';
import { inject as service } from '@ember/service';
import AccountService from 'cio/services/account';
import { tracked } from '@glimmer/tracking';
import EnvironmentService from 'cio/services/environment';
import ApiService from 'cio/services/api';
import { Task } from 'ember-concurrency';
import CampaignModel, {
  Edge,
  CampaignState,
  CampaignType,
  RESTRICTED_ACTION_TYPES,
  TYPE_NAMES,
  TYPE_NAME_ARTICLES,
} from 'cio/models/campaign';
import ActionModel, { ActionType } from 'cio/models/action';
import DS from 'ember-data';
import { WorkflowGraph } from 'cio/utils/visual-workflow-builder/workflow-graph';
import { addWarningsToActions, updateActionsBasedOnWarnings } from 'cio/utils/visual-workflow-builder/copying';
import EnvironmentModel from 'cio/models/environment';
import { WorkflowCopyModalActions } from 'cio/utils/analytics/transforms/campaign';
import isEqual from 'lodash.isequal';
import { seriesString } from 'cio/utils/formatters';
import actionInfo from 'cio/utils/action-info';
import { pluralize } from 'ember-inflector';
import { lowercase } from 'cio/helpers/lowercase';
import { capitalize } from '@ember/string';
import flattenArray from 'cio/utils/flatten-array';
import { CampaignFilterModel } from 'cio/services/filters';
import { isEmpty } from '@ember/utils';
import { sortByCaseInsensitive } from 'cio/utils/sort';

interface VisualWorkflowBuilderV2BaseCopyModalArgs {
  campaign: CampaignModel;
  title: string;
  isCopyFrom: boolean;
  isCopyTo: boolean;
  showSpinnerOverlay: boolean;
  highlightedNodeIds: string[];
  onUpdateHighlightedNodeIds?: (highlightedNodeIds: string[]) => unknown;
  persistCopyTo?: Task<unknown[], [any, any[], string]>;
  onUndo?: () => unknown;
  onClose: () => unknown;
  onSave: () => unknown;
  onCloseReviewModal?: () => unknown;
  onSelectEnvironment: (environmentId: string) => unknown;
  onSelectCampaign: (campaignId: string) => unknown;
  onActionsFetched?: (campaignModel: CampaignModel | null, actionModels: ActionModel[]) => unknown;
  onCopyToFinished?: (serializedCampaign: any, serializedActions: any[]) => unknown;
  isSelectEntireWorkflowCheckboxChecked?: boolean;
  highlightEntireWorkflow?: () => unknown;
  onCanPerformHighlightOperationChanged?: ({
    canCopy,
    canMove,
    canDelete,
  }: {
    canCopy: boolean;
    canMove: boolean;
    canDelete: boolean;
  }) => unknown;
  trackEvent: (modalActionType: WorkflowCopyModalActions) => unknown;
  hasReadOnlyPermission: boolean;
}

const CAMPAIGN_TYPES_WITH_RESTRICTIONS = Object.keys(RESTRICTED_ACTION_TYPES);

function prepareCampaignWithRestrictedActionsWarning(
  campaign: CampaignModel | Record<string, any>,
  actionTypeString: string,
) {
  let maybeLowercasedTypeName =
    campaign.type === CampaignType.APITriggered
      ? (TYPE_NAMES as any)[campaign.type]
      : lowercase([(TYPE_NAMES as any)[campaign.type]]);

  let typeNameArticle = (TYPE_NAME_ARTICLES as any)[campaign.type];

  let campaignTypeString = `${typeNameArticle} ${maybeLowercasedTypeName}`;

  return `${capitalize(actionTypeString)} cannot be copied into ${campaignTypeString}`;
}

function prepareActionTypesString(actionTypes: string[]) {
  return seriesString(
    actionTypes
      .map((type) => {
        let info = actionInfo(type as ActionType);

        if (info.group === 'delays') {
          return 'delays';
        }

        return pluralize(info.shortName);
      })
      .uniq()
      .sort()
      .join(','),
  );
}

export default class VisualWorkflowBuilderV2BaseCopyModal extends Component<VisualWorkflowBuilderV2BaseCopyModalArgs> {
  @service account!: AccountService;
  @service api!: ApiService;
  @service store!: DS.Store;
  @service environment!: EnvironmentService;

  @tracked selectedEnvironmentId = '';
  @tracked selectedCampaignId = '';

  @tracked fakeCampaignModel: CampaignModel | null = null;
  @tracked fakeActionModels: ActionModel[] | null = null;

  @tracked fakeCampaignGraph: WorkflowGraph | null = null;

  @tracked isShowingReviewModal = false;
  @tracked isShowingManageLanguagesModal = false;

  @tracked warnings: string[] = [];

  get environmentDropdownItems() {
    return sortByCaseInsensitive(
      (this.account.current.currentUser.accessibleEnvironments ?? []).map((environment) => ({
        label: environment.name,
        value: environment.id,
        model: environment,
      })),
      ['label'],
    );
  }

  @tracked campaignDropdownItems: { label: string; value: string }[] = [];

  constructor(owner: unknown, args: VisualWorkflowBuilderV2BaseCopyModalArgs) {
    super(owner, args);

    this.selectedEnvironmentId = this.environment.current.id ?? '';
    this.environmentForManageLanguages = this.environment.current as EnvironmentModel;
  }

  fetchCampaigns = task({ restartable: true }, async () => {
    let result = await this.api.request('deliveries/filters', {
      replaceEnvironmentID: this.selectedEnvironmentId,
      data: {
        type: 'campaigns',
      },
    });

    this.campaignDropdownItems = sortByCaseInsensitive(
      result.campaigns
        .filter(
          (serializedCampaign: CampaignFilterModel) =>
            serializedCampaign.type !== CampaignType.None && serializedCampaign.state !== CampaignState.Archived,
        )
        .map((serializedCampaign: CampaignFilterModel) => {
          let item = {
            label: serializedCampaign.name,
            value: serializedCampaign.id,
            disabled: false,
            tooltipContent: '',
          };

          let campaignType = serializedCampaign.type as CampaignType;

          if (CAMPAIGN_TYPES_WITH_RESTRICTIONS.includes(campaignType) && this.hasPotentiallyRestrictedActionTypes) {
            let restrictedTypes = (RESTRICTED_ACTION_TYPES as any)[campaignType] ?? [];
            let highlightedRestrictedTypes = this.highlightedActionTypes.filter((type) =>
              restrictedTypes.includes(type),
            );

            if (highlightedRestrictedTypes.length > 0) {
              item.disabled = true;
              item.tooltipContent = prepareCampaignWithRestrictedActionsWarning(
                serializedCampaign,
                prepareActionTypesString(highlightedRestrictedTypes as string[]),
              );
            }
          }

          return item;
        }),
      ['label'],
    );
  });

  @tracked serializedSelectedCampaign: Record<string, any> | null = null;
  @tracked serializedCampaignActions: Record<string, any>[] | null = null;

  fetchActions = task({ restartable: true }, async () => {
    this.clearFakeModels();

    let result = await this.api.request(`campaigns/${this.selectedCampaignId}`, {
      replaceEnvironmentID: this.selectedEnvironmentId,
    });

    this.serializedSelectedCampaign = {
      ...result.campaign,
      id: `copy_${result.campaign.id}`,
      workflow_action_ids: result.campaign.workflow_action_ids.map((id: string) => `copy_${id}`),
      edges: result.campaign.edges.map((edge: Edge) => ({
        ...edge,
        from: `copy_${edge.from}`,
        to: `copy_${edge.to}`,
      })),
      _copy: true,
    };

    this.serializedCampaignActions = result.actions.map((action: any) => ({
      ...action,
      id: `copy_${action.id}`,
      campaign_id: `copy_${action.campaign_id}`,
      action_ids: action.action_ids?.map((id: string) => `copy_${id}`),
      template_id: action.template_id ? `copy_${action.template_id}` : null,
      _copy: true,
    }));

    this.store.pushPayload({
      campaign: this.serializedSelectedCampaign,
      actions: this.serializedCampaignActions,
    });

    this.fakeCampaignModel = this.store.peekRecord('campaign', `copy_${result.campaign.id}`);
    this.fakeActionModels = this.fakeCampaignModel?.actions.toArray() ?? [];

    if (this.args.onActionsFetched) {
      this.args.onActionsFetched(this.fakeCampaignModel, this.fakeActionModels);
    }
  });

  @action
  onSelectEnvironment(environmentItem: { label: string; value: string }) {
    if (this.selectedEnvironmentId !== environmentItem.value) {
      this.selectedCampaignId = '';
      this.clearFakeModels();
    }
    this.selectedEnvironmentId = environmentItem.value;

    this.args.trackEvent(WorkflowCopyModalActions.WorkspaceSelected);

    this.fetchCampaigns.perform();
    this.fetchEnvironmentToSetLanguage.perform();

    this.args.onSelectEnvironment(environmentItem.value);
  }

  @action
  onSelectCampaign(campaignItem: { label: string; value: string }) {
    this.selectedCampaignId = campaignItem.value;

    this.args.trackEvent(WorkflowCopyModalActions.CampaignSelected);

    this.fetchActions.perform();

    this.args.onSelectCampaign(campaignItem.value);
  }

  private clearFakeModels() {
    this.fakeCampaignModel?.unloadRecord();
    this.fakeActionModels?.forEach((actionModel) => actionModel.unloadRecord());

    this.fakeCampaignModel = null;
    this.fakeActionModels = null;

    this.serializedSelectedCampaign = null;
    this.serializedCampaignActions = null;
  }

  @action
  closeReviewModal() {
    this.isShowingReviewModal = false;
    this.warnings = [];

    if (this.args.onCloseReviewModal) {
      this.args.onCloseReviewModal();
    }
  }

  @action
  confirmReviewModal() {
    this.args.onSave();
  }

  @computed('args.highlightedNodeIds.[]')
  get highlightedActionTypes() {
    return this.args.highlightedNodeIds
      .map((id) => this.store.peekRecord('action', id)?.type)
      .compact()
      .uniq();
  }

  @computed('highlightedActionTypes')
  get hasPotentiallyRestrictedActionTypes() {
    let allRestrictedActionTypes = flattenArray(Object.values(RESTRICTED_ACTION_TYPES)).uniq();

    return this.highlightedActionTypes.some((type) => allRestrictedActionTypes.includes(type));
  }

  @computed('args.isCopyFrom', 'args.campaign', 'fakeCampaignModel')
  get sourceCampaign() {
    return this.args.isCopyFrom ? this.fakeCampaignModel : this.args.campaign;
  }

  @computed('args.isCopyFrom', 'args.campaign', 'fakeCampaignModel')
  get targetCampaign() {
    return this.args.isCopyFrom ? this.args.campaign : this.fakeCampaignModel;
  }

  @computed('highlightedRestrictedTypes.[]')
  get canCopySelectedActionTypes() {
    return this.highlightedRestrictedTypes.length === 0;
  }

  @computed('args.highlightedNodeIds.[]', 'targetCampaign.type')
  get highlightedRestrictedTypes() {
    if (!this.targetCampaign) {
      return [];
    }

    let restrictedTypes = (RESTRICTED_ACTION_TYPES as any)[this.targetCampaign.type] ?? [];

    let highlightedTypes = this.args.highlightedNodeIds
      .map((id) => this.store.peekRecord('action', id)?.type)
      .compact()
      .uniq();

    return highlightedTypes.filter((type) => restrictedTypes.includes(type));
  }

  @computed('highlightedRestrictedTypes.[]', 'targetCampaign.type')
  get restrictedActionTypesWarningString() {
    if (!this.targetCampaign) {
      return '';
    }

    return prepareCampaignWithRestrictedActionsWarning(
      this.targetCampaign,
      prepareActionTypesString(this.highlightedRestrictedTypes as string[]),
    );
  }

  @tracked environmentForManageLanguages: EnvironmentModel | null = null;

  fetchEnvironmentToSetLanguage = task({ restartable: true }, async () => {
    if (this.args.isCopyTo && !isEmpty(this.selectedEnvironmentId)) {
      this.environmentForManageLanguages = await this.store.findRecord('environment', this.selectedEnvironmentId, {
        reload: true,
      });
    } else {
      this.environmentForManageLanguages = this.environment.current as EnvironmentModel;
    }
  });

  private addWarnings() {
    let sourceEnvironment: EnvironmentModel | null = null;
    let targetEnvironment: EnvironmentModel | null = null;

    if (this.args.isCopyFrom) {
      sourceEnvironment = this.store.peekRecord('environment', this.selectedEnvironmentId) ?? null;
      targetEnvironment = this.environment.current.id ? (this.environment.current as EnvironmentModel) : null;
    } else if (this.args.isCopyTo) {
      sourceEnvironment = this.environment.current.id ? (this.environment.current as EnvironmentModel) : null;
      targetEnvironment = this.store.peekRecord('environment', this.selectedEnvironmentId) ?? null;
    }

    if (!sourceEnvironment || !targetEnvironment || !this.fakeActionModels) {
      throw new Error('No workspace');
    }

    let { sourceCampaign, targetCampaign } = this;

    if (sourceCampaign == null || targetCampaign == null) {
      return [];
    }

    let actionModels = this.args.highlightedNodeIds.map((id) => this.store.peekRecord('action', id)!);
    let childActions = actionModels.reduce((childActions, actionModel) => {
      if (!actionModel.isSplitTest) {
        return childActions;
      }

      childActions = [...childActions, ...actionModel.actions.toArray()];
      return childActions;
    }, [] as ActionModel[]);

    let allActionModels = [...actionModels, ...childActions];

    let allWarnings = addWarningsToActions(
      allActionModels,
      sourceCampaign,
      targetCampaign,
      sourceEnvironment,
      targetEnvironment,
      this.args.isCopyFrom,
    );

    if (this.args.isCopyFrom) {
      updateActionsBasedOnWarnings(allActionModels);
    }

    return allWarnings;
  }

  @action
  checkWarningsBeforeConfirm() {
    if (this.warnings.includes('lang_attribute')) {
      this.isShowingManageLanguagesModal = true;
      return;
    }

    return this.confirmReviewModal();
  }

  @action
  closeManageLanguagesModal() {
    this.isShowingManageLanguagesModal = false;

    if (this.environmentForManageLanguages?.hasLanguageAttribute !== true) {
      this.onClose();
    } else {
      this.confirmReviewModal();
    }
  }

  @action
  save() {
    this.warnings = this.addWarnings();

    if (this.warnings.length > 0) {
      this.isShowingReviewModal = true;
    } else {
      this.args.onSave();
    }
  }

  @action
  onUpdateHighlightedNodeIds(highlightedNodeIds: string[]) {
    if (!this.args.onUpdateHighlightedNodeIds) {
      return;
    }

    let oldHighlightedNodeIds = [...this.args.highlightedNodeIds].sort();

    this.args.onUpdateHighlightedNodeIds(highlightedNodeIds);

    if (!isEqual([...highlightedNodeIds].sort(), oldHighlightedNodeIds)) {
      this.args.trackEvent(WorkflowCopyModalActions.WorkflowItemsSelected);
    }
  }

  @action
  onClose() {
    this.args.trackEvent(WorkflowCopyModalActions.ModalClosed);
    this.args.onClose();
  }

  @action
  highlightEntireWorkflow() {
    if (!this.args.highlightEntireWorkflow) {
      return;
    }

    this.args.trackEvent(WorkflowCopyModalActions.SelectEntireWorkflowClicked);
    this.args.highlightEntireWorkflow();
  }

  @action
  onCopyToFinished(serializedCampaign: any, serializedActions: any[]) {
    if (!this.args.onCopyToFinished) {
      return;
    }

    this.args.trackEvent(WorkflowCopyModalActions.PlacementChosen);
    this.args.onCopyToFinished(serializedCampaign, serializedActions);
  }
}
